import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, EventEmitter, Directive, Input, Output, ContentChildren, NgModule } from '@angular/core';
import { Observable, ReplaySubject, BehaviorSubject, of, merge } from 'rxjs';
import { share, shareReplay, filter, distinctUntilChanged, switchMap, take, tap, map } from 'rxjs/operators';
import Rive from 'rive-canvas';
import * as i1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';

// Observable that trigger on every frame
const animationFrame = new Observable((subscriber) => {
    let start = 0;
    let first = true;
    const run = (time) => {
        const delta = time - start;
        start = time;
        if (first) {
            subscriber.next(16);
            first = false;
        }
        else {
            subscriber.next(delta);
        }
        // Because of bug in Chrome first value might be too big and cause issues
        if (subscriber.closed)
            return;
        requestAnimationFrame(run);
    };
    requestAnimationFrame(run);
});

const RIVE_FOLDER = new InjectionToken('Folder with Rive files');
const RIVE_VERSION = new InjectionToken('Version used to load rive WASM');

class RiveService {
    constructor(folder, version, http) {
        this.folder = folder;
        this.version = version;
        this.http = http;
        this.files = {};
        this.frame = animationFrame.pipe(share());
        this.folder = folder ?? 'assets/rive';
        this.version = version ?? '0.7.6';
    }
    async getRive() {
        if (!this.rive) {
            const locateFile = (file) => `https://unpkg.com/rive-canvas@${this.version}/${file}`;
            this.rive = await Rive({ locateFile });
        }
        return this.rive;
    }
    async load(file) {
        if (typeof file !== 'string') {
            const [rive, buffer] = await Promise.all([
                this.getRive(),
                file.arrayBuffer(),
            ]);
            return rive?.load(new Uint8Array(buffer));
        }
        if (!this.files[file]) {
            const asset = `${this.folder}/${file}.riv`;
            const [rive, buffer] = await Promise.all([
                this.getRive(),
                this.http.get(asset, { responseType: 'arraybuffer' }).toPromise(),
            ]);
            if (!rive)
                throw new Error('Could not load rive');
            this.files[file] = rive.load(new Uint8Array(buffer));
        }
        return this.files[file];
    }
}
RiveService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveService, deps: [{ token: RIVE_FOLDER, optional: true }, { token: RIVE_VERSION, optional: true }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
RiveService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [RIVE_FOLDER]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [RIVE_VERSION]
                }] }, { type: i1.HttpClient }]; } });

function toInt(value) {
    const v = typeof value === 'string' ? parseInt(value) : value;
    if (typeof v !== 'number')
        return;
    return v;
}
function toFloat(value) {
    const v = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof v !== 'number')
        return;
    return v;
}
function toBool(value) {
    if (value === '' || value === true)
        return true;
    if (value === false)
        return false;
    return;
}

const exist$3 = (v) => v !== null && v !== undefined;
// Observable that trigger once when element is visible
const onVisible = (element) => new Observable((subscriber) => {
    // SSR
    if (typeof window === 'undefined') {
        subscriber.next(false);
        subscriber.complete();
        return;
    }
    // Compatibility
    if (!('IntersectionObserver' in window)) {
        subscriber.next(true);
        subscriber.complete();
        return;
    }
    let isVisible = false;
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const visible = entry.intersectionRatio !== 0;
            if (visible !== isVisible) {
                isVisible = !isVisible;
                subscriber.next(isVisible);
                subscriber.complete();
            }
        });
    }, { threshold: [0] });
    // start observing element visibility
    observer.observe(element);
});
// Force event to run inside zones
function enterZone(zone) {
    return (source) => new Observable(observer => source.subscribe({
        next: (x) => zone.run(() => observer.next(x)),
        error: (err) => observer.error(err),
        complete: () => observer.complete()
    }));
}
class RiveCanvasDirective {
    constructor(zone, service, element) {
        this.zone = zone;
        this.service = service;
        this.url = new ReplaySubject();
        this.arboardName = new BehaviorSubject(null);
        this.boxes = {};
        this.viewbox = '0 0 100% 100%';
        this.lazy = false;
        this.fit = 'contain';
        this.alignment = 'center';
        this.artboardChange = new EventEmitter();
        this.canvas = element.nativeElement;
        this.isVisible = onVisible(element.nativeElement).pipe(shareReplay({ bufferSize: 1, refCount: true }), enterZone(this.zone));
        this.loaded = this.url.pipe(filter(exist$3), distinctUntilChanged(), filter(() => typeof window !== 'undefined' && !!this.ctx), // Make sure it's not ssr
        switchMap(async (url) => {
            this.file = await this.service.load(url);
            this.rive = this.service.rive;
            if (!this.rive)
                throw new Error('Service could not load rive');
            this.renderer = new this.rive.CanvasRenderer(this.ctx);
        }), switchMap(_ => this.setArtboard()), shareReplay({ bufferSize: 1, refCount: true }));
    }
    set riv(url) {
        this.url.next(url);
    }
    set name(name) {
        this.arboardName.next(name);
    }
    set width(w) {
        this.canvas.width = toInt(w) ?? this.canvas.width;
    }
    get width() {
        return this.canvas.width;
    }
    set height(h) {
        this.canvas.height = toInt(h) ?? this.canvas.height;
    }
    get height() {
        return this.canvas.height;
    }
    ngOnInit() {
        this.onReady().pipe(take(1)).subscribe();
    }
    ngOnDestroy() {
        this.artboard?.delete();
    }
    get ctx() {
        if (!this._ctx) {
            this._ctx = this.canvas.getContext('2d');
        }
        return this._ctx;
    }
    setArtboard() {
        return this.arboardName.pipe(tap(() => this.artboard?.delete()), // Remove previous artboard if any
        map(name => name ? this.file?.artboardByName(name) : this.file?.defaultArtboard()), tap(artboard => this.artboard = artboard?.instance()), tap(() => this.artboardChange.emit(this.artboard)), map(() => true));
    }
    /**
     * Calculate the box of the canvas based on viewbox, width and height
     * It memorizes the values to avoid recalculation for each frame
     */
    get box() {
        const w = this.width;
        const h = this.height;
        const boxId = `${this.viewbox} ${w} ${h}`;
        if (!this.boxes[boxId]) {
            const bounds = this.viewbox.split(' ');
            if (bounds.length !== 4)
                throw new Error('View box should look like "0 0 100% 100%"');
            const [minX, minY, maxX, maxY] = bounds.map((v, i) => {
                const size = i % 2 === 0 ? w : h;
                const percentage = v.endsWith('%')
                    ? parseInt(v.slice(0, -1), 10) / 100
                    : parseInt(v, 10) / size;
                return i < 2 ? -size * percentage : size / percentage;
            });
            this.boxes[boxId] = { minX, minY, maxX, maxY };
        }
        return this.boxes[boxId];
    }
    get isLazy() {
        return this.lazy === true || this.lazy === '';
    }
    get count() {
        return this.artboard?.animationCount();
    }
    onReady() {
        if (this.isLazy) {
            return this.isVisible.pipe(filter(visible => !!visible), take(1), switchMap(() => this.loaded));
        }
        return this.loaded;
    }
    draw(instance, delta, mix) {
        if (!this.rive)
            throw new Error('Could not load rive before registrating instance');
        if (!this.artboard)
            throw new Error('Could not load artboard before registrating instance');
        if (!this.renderer)
            throw new Error('Could not load renderer before registrating instance');
        // Move frame
        if (isLinearAnimation(instance)) {
            instance.advance(delta);
            instance.apply(this.artboard, mix ?? 1);
        }
        else {
            instance.advance(this.artboard, delta);
        }
        this.artboard.advance(delta);
        // Render frame on canvas
        const fit = this.rive.Fit[this.fit];
        const alignment = this.rive.Alignment[this.alignment];
        const box = this.box;
        const bounds = this.artboard.bounds;
        // Align renderer if needed
        this.ctx.restore();
        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.save();
        this.renderer.align(fit, alignment, box, bounds);
        this.ctx.clearRect(bounds.minX, bounds.minY, bounds.maxX, bounds.maxY);
        this.artboard.draw(this.renderer);
    }
}
RiveCanvasDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveCanvasDirective, deps: [{ token: i0.NgZone }, { token: RiveService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
RiveCanvasDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveCanvasDirective, selector: "canvas[riv]", inputs: { riv: "riv", name: ["artboard", "name"], viewbox: "viewbox", lazy: "lazy", fit: "fit", alignment: "alignment", height: "height" }, outputs: { artboardChange: "artboardChange" }, exportAs: ["rivCanvas"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveCanvasDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'canvas[riv]',
                    exportAs: 'rivCanvas'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveService }, { type: i0.ElementRef }]; }, propDecorators: { riv: [{
                type: Input
            }], name: [{
                type: Input,
                args: ['artboard']
            }], viewbox: [{
                type: Input
            }], lazy: [{
                type: Input
            }], fit: [{
                type: Input
            }], alignment: [{
                type: Input
            }], height: [{
                type: Input
            }], artboardChange: [{
                type: Output
            }] } });
function isLinearAnimation(instance) {
    return 'didLoop' in instance;
}

function getRivePlayerState(state = {}) {
    return {
        speed: 1,
        playing: false,
        mix: 1,
        autoreset: false,
        ...state
    };
}
function frameToSec(frame, fps) {
    return frame / fps;
}
function round(value) {
    return Math.round((value + Number.EPSILON) * 10000) / 10000;
}
function exist$2(v) {
    return v !== undefined && v !== null;
}
class RivePlayer {
    constructor(zone, canvas, service) {
        this.zone = zone;
        this.canvas = canvas;
        this.service = service;
        this.distance = new BehaviorSubject(null);
        this.state = new BehaviorSubject(getRivePlayerState());
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.load = new EventEmitter();
        this.timeChange = new EventEmitter();
        this.playChange = new EventEmitter();
        this.speedChange = new EventEmitter();
    }
    set name(name) {
        if (typeof name !== 'string')
            return;
        this.zone.runOutsideAngular(() => {
            this.register(name);
        });
    }
    set index(value) {
        const index = typeof value === 'string' ? parseInt(value) : value;
        if (typeof index !== 'number')
            return;
        this.zone.runOutsideAngular(() => {
            this.register(index);
        });
    }
    set mix(value) {
        const mix = typeof value === 'string' ? parseFloat(value) : value;
        if (mix && mix >= 0 && mix <= 1)
            this.update({ mix });
    }
    get mix() {
        return this.state.getValue().mix;
    }
    set mode(mode) {
        if (mode)
            this.update({ mode });
    }
    get mode() {
        return this.state.getValue().mode;
    }
    // NUMBERS
    set speed(value) {
        const speed = typeof value === 'string' ? parseFloat(value) : value;
        if (typeof speed === 'number')
            this.update({ speed });
    }
    get speed() {
        return this.state.getValue().speed;
    }
    set play(playing) {
        if (playing === true || playing === '') {
            this.update({ playing: true });
        }
        else if (playing === false) {
            this.update({ playing: false });
        }
    }
    get play() {
        return this.state.getValue().playing;
    }
    set autoreset(autoreset) {
        if (autoreset === true || autoreset === '') {
            this.update({ autoreset: true });
        }
        else if (autoreset === false) {
            this.update({ autoreset: false });
        }
    }
    get autoreset() {
        return this.state.getValue().autoreset;
    }
    set time(value) {
        const time = typeof value === 'string' ? parseFloat(value) : value;
        if (typeof time === 'number')
            this.distance.next(time);
    }
    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.instance?.delete();
    }
    update(state) {
        const next = getRivePlayerState({ ...this.state.getValue(), ...state });
        this.state.next(next);
    }
    initAnimation(name) {
        if (!this.canvas.rive)
            throw new Error('Could not load animation instance before rive');
        if (!this.canvas.artboard)
            throw new Error('Could not load animation instance before artboard');
        this.animation = typeof name === 'string'
            ? this.canvas.artboard.animationByName(name)
            : this.canvas.artboard.animationByIndex(name);
        this.startTime = round((this.animation.workStart === -1 ? 0 : this.animation.workStart) / this.animation.fps);
        this.endTime = round((this.animation.workEnd === -1 ? this.animation.duration : this.animation.workEnd) / this.animation.fps);
        this.instance = new this.canvas.rive.LinearAnimationInstance(this.animation);
        this.load.emit(this.animation);
    }
    getTime() {
        if (!this.instance)
            throw new Error('Could not load animation instance before running it');
        return this.instance.time;
    }
    getFrame(state) {
        if (state.playing) {
            return this.service.frame.pipe(map((time) => [state, time]));
        }
        else {
            return of(null);
        }
    }
    register(name) {
        // Stop subscribing to previous animation if any
        this.sub?.unsubscribe();
        // Update if time have changed from the input
        const onTimeChange = this.distance.pipe(filter(exist$2), filter(time => time !== this.getTime()), distinctUntilChanged(), map(time => time - this.getTime()));
        // Update on frame change if playing
        const onFrameChange = this.state.pipe(switchMap((state) => this.getFrame(state)), filter(exist$2), map(([state, time]) => this.moveFrame(state, time)));
        // Wait for canvas & animation to be loaded
        this.sub = this.canvas.onReady().pipe(map(() => this.initAnimation(name)), switchMap(() => merge(onTimeChange, onFrameChange))).subscribe((delta) => this.applyChange(delta));
    }
    moveFrame(state, time) {
        if (!this.animation)
            throw new Error('Could not load animation before running it');
        if (!this.instance)
            throw new Error('Could not load animation instance before running it');
        const { speed, autoreset, mode } = state;
        // Default mode, don't apply any logic
        if (!mode)
            return time / 1000 * speed;
        let delta = (time / 1000) * speed;
        // Round to avoid JS error on division
        const start = this.startTime ?? 0;
        const end = this.endTime ?? (this.animation.duration / this.animation.fps);
        const currentTime = round(this.instance.time);
        // When player hit floor
        if (currentTime + delta < start) {
            if (mode === 'loop' && speed < 0 && end) {
                delta = end - currentTime; // end - currentTime
            }
            else if (mode === 'ping-pong') {
                delta = -delta;
                this.update({ speed: -speed });
                this.zone.run(() => this.speedChange.emit(-speed));
            }
            else if (mode === 'one-shot') {
                this.update({ playing: false });
                this.zone.run(() => this.playChange.emit(false));
                delta = start - currentTime;
            }
        }
        // Put before "hit last frame" else currentTime + delta > end
        if (mode === 'one-shot' && autoreset) {
            if (speed > 0 && currentTime === end) {
                delta = start - end;
            }
            if (speed < 0 && currentTime === start) {
                delta = end - start;
            }
        }
        // When player hit last frame
        if (currentTime + delta > end) {
            if (mode === 'loop' && speed > 0) {
                delta = start - currentTime;
            }
            else if (mode === 'ping-pong') {
                delta = -delta;
                this.update({ speed: -speed });
                this.zone.run(() => this.speedChange.emit(-speed));
            }
            else if (mode === 'one-shot') {
                this.update({ playing: false });
                this.zone.run(() => this.playChange.emit(false));
                delta = end - currentTime;
            }
        }
        return delta;
    }
    applyChange(delta) {
        if (!this.instance)
            throw new Error('Could not load animation instance before running it');
        this.canvas.draw(this.instance, delta, this.state.getValue().mix);
    }
}
RivePlayer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RivePlayer, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }, { token: RiveService }], target: i0.ɵɵFactoryTarget.Directive });
RivePlayer.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RivePlayer, selector: "riv-player, [rivPlayer]", inputs: { name: "name", index: "index", mix: "mix", mode: "mode", speed: "speed", play: "play", autoreset: "autoreset", time: "time" }, outputs: { load: "load", timeChange: "timeChange", playChange: "playChange", speedChange: "speedChange" }, exportAs: ["rivPlayer"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RivePlayer, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-player, [rivPlayer]',
                    exportAs: 'rivPlayer'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }, { type: RiveService }]; }, propDecorators: { name: [{
                type: Input
            }], index: [{
                type: Input
            }], mix: [{
                type: Input
            }], mode: [{
                type: Input
            }], speed: [{
                type: Input
            }], play: [{
                type: Input
            }], autoreset: [{
                type: Input
            }], time: [{
                type: Input
            }], load: [{
                type: Output
            }], timeChange: [{
                type: Output
            }], playChange: [{
                type: Output
            }], speedChange: [{
                type: Output
            }] } });

function getRiveAnimationState(state = {}) {
    return {
        speed: 1,
        playing: false,
        mix: 1,
        ...state
    };
}
function exist$1(v) {
    return v !== undefined && v !== null;
}
class RiveAnimationDirective {
    constructor(zone, canvas, service) {
        this.zone = zone;
        this.canvas = canvas;
        this.service = service;
        this.distance = new BehaviorSubject(null);
        this.state = new BehaviorSubject(getRiveAnimationState());
        this.load = new EventEmitter();
    }
    set name(name) {
        if (typeof name !== 'string')
            return;
        this.zone.runOutsideAngular(() => {
            this.register(name);
        });
    }
    set index(value) {
        const index = typeof value === 'string' ? parseInt(value) : value;
        if (typeof index !== 'number')
            return;
        this.zone.runOutsideAngular(() => {
            this.register(index);
        });
    }
    set mix(value) {
        const mix = typeof value === 'string' ? parseFloat(value) : value;
        if (mix && mix >= 0 && mix <= 1)
            this.update({ mix });
    }
    get mix() {
        return this.state.getValue().mix;
    }
    // NUMBERS
    set speed(value) {
        const speed = typeof value === 'string' ? parseFloat(value) : value;
        if (typeof speed === 'number')
            this.update({ speed });
    }
    get speed() {
        return this.state.getValue().speed;
    }
    set play(playing) {
        if (playing === true || playing === '') {
            this.update({ playing: true });
        }
        else if (playing === false) {
            this.update({ playing: false });
        }
    }
    get play() {
        return this.state.getValue().playing;
    }
    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.instance?.delete();
    }
    update(state) {
        const next = getRiveAnimationState({ ...this.state.getValue(), ...state });
        this.state.next(next);
    }
    initAnimation(name) {
        if (!this.canvas.rive)
            throw new Error('Could not load animation instance before rive');
        if (!this.canvas.artboard)
            throw new Error('Could not load animation instance before artboard');
        this.animation = typeof name === 'string'
            ? this.canvas.artboard.animationByName(name)
            : this.canvas.artboard.animationByIndex(name);
        this.instance = new this.canvas.rive.LinearAnimationInstance(this.animation);
        this.load.emit(this.animation);
    }
    getFrame(state) {
        if (state.playing) {
            return this.service.frame.pipe(map((time) => [state, time]));
        }
        else {
            return of(null);
        }
    }
    register(name) {
        // Stop subscribing to previous animation if any
        this.sub?.unsubscribe();
        // Update on frame change if playing
        const onFrameChange = this.state.pipe(switchMap((state) => this.getFrame(state)), filter(exist$1), map(([state, time]) => this.moveFrame(state, time)));
        // Wait for canvas & animation to be loaded
        this.sub = this.canvas.onReady().pipe(map(() => this.initAnimation(name)), switchMap(() => onFrameChange)).subscribe((delta) => this.applyChange(delta));
    }
    moveFrame(state, time) {
        if (!this.animation)
            throw new Error('Could not load animation before running it');
        if (!this.instance)
            throw new Error('Could not load animation instance before running it');
        return (time / 1000) * state.speed;
    }
    applyChange(delta) {
        if (!this.instance)
            throw new Error('Could not load animation instance before running it');
        this.canvas.draw(this.instance, delta, this.state.getValue().mix);
    }
}
RiveAnimationDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveAnimationDirective, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }, { token: RiveService }], target: i0.ɵɵFactoryTarget.Directive });
RiveAnimationDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveAnimationDirective, selector: "riv-animation, [rivAnimation]", inputs: { name: "name", index: "index", mix: "mix", speed: "speed", play: "play" }, outputs: { load: "load" }, exportAs: ["rivAnimation"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveAnimationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-animation, [rivAnimation]',
                    exportAs: 'rivAnimation'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }, { type: RiveService }]; }, propDecorators: { name: [{
                type: Input
            }], index: [{
                type: Input
            }], mix: [{
                type: Input
            }], speed: [{
                type: Input
            }], play: [{
                type: Input
            }], load: [{
                type: Output
            }] } });

class RiveTransformComponent {
    constructor(zone, canvas) {
        this.zone = zone;
        this.canvas = canvas;
        this.state = {};
    }
    set name(name) {
        if (typeof name !== 'string')
            return;
        this.canvas.onReady().subscribe(() => {
            this.component = this.getComponent(name);
            if (!this.component)
                throw new Error(`Could not find component with name: "${name}"`);
            for (const key in this.state) {
                this.component[key] = this.state[key];
            }
        });
    }
    set scale(value) {
        this.set('scaleX', value);
        this.set('scaleY', value);
    }
    set scaleX(value) {
        this.set('scaleX', value);
    }
    set scaleY(value) {
        this.set('scaleX', value);
    }
    set rotation(value) {
        const v = typeof value === 'string' ? parseFloat(value) : value;
        if (v) {
            const rotation = Math.abs(v) > (2 * Math.PI) ? (v * (Math.PI / 180)) : v;
            this.set('rotation', rotation);
        }
    }
    set(key, value) {
        this.zone.runOutsideAngular(() => {
            const v = typeof value === 'string' ? parseFloat(value) : value;
            if (typeof v === 'number') {
                if (this.component)
                    this.component[key] = v;
                else
                    this.state[key] = v;
            }
        });
    }
}
RiveTransformComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveTransformComponent, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }], target: i0.ɵɵFactoryTarget.Directive });
RiveTransformComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveTransformComponent, inputs: { name: "name", scale: "scale", scaleX: "scaleX", scaleY: "scaleY", rotation: "rotation" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveTransformComponent, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }]; }, propDecorators: { name: [{
                type: Input
            }], scale: [{
                type: Input
            }], scaleX: [{
                type: Input
            }], scaleY: [{
                type: Input
            }], rotation: [{
                type: Input
            }] } });

class RiveNode extends RiveTransformComponent {
    constructor(zone, canvas) {
        super(zone, canvas);
    }
    set x(value) {
        this.set('x', value);
    }
    get x() {
        return this.component?.x;
    }
    set y(value) {
        this.set('y', value);
    }
    get y() {
        return this.component?.y;
    }
    getComponent(name) {
        return this.canvas.artboard?.node(name);
    }
}
RiveNode.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveNode, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }], target: i0.ɵɵFactoryTarget.Directive });
RiveNode.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveNode, selector: "riv-node, [rivNode]", inputs: { x: "x", y: "y" }, exportAs: ["rivNode"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveNode, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-node, [rivNode]',
                    exportAs: 'rivNode'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }]; }, propDecorators: { x: [{
                type: Input
            }], y: [{
                type: Input
            }] } });

class RiveBone extends RiveTransformComponent {
    constructor(zone, canvas) {
        super(zone, canvas);
    }
    set length(value) {
        this.set('length', value);
    }
    getComponent(name) {
        return this.canvas.artboard?.bone(name);
    }
}
RiveBone.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveBone, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }], target: i0.ɵɵFactoryTarget.Directive });
RiveBone.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveBone, selector: "riv-bone, [rivBone]", inputs: { length: "length" }, exportAs: ["rivBone"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveBone, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-bone, [rivBone]',
                    exportAs: 'rivBone'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }]; }, propDecorators: { length: [{
                type: Input
            }] } });

class RiveRootBone extends RiveTransformComponent {
    constructor(zone, canvas) {
        super(zone, canvas);
    }
    set x(value) {
        this.set('x', value);
    }
    set y(value) {
        this.set('y', value);
    }
    set length(value) {
        this.set('length', value);
    }
    getComponent(name) {
        return this.canvas.artboard?.rootBone(name);
    }
}
RiveRootBone.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveRootBone, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }], target: i0.ɵɵFactoryTarget.Directive });
RiveRootBone.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveRootBone, selector: "riv-root-bone, [rivRootBone]", inputs: { x: "x", y: "y", length: "length" }, exportAs: ["rivRootBone"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveRootBone, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-root-bone, [rivRootBone]',
                    exportAs: 'rivRootBone'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }]; }, propDecorators: { x: [{
                type: Input
            }], y: [{
                type: Input
            }], length: [{
                type: Input
            }] } });

function getInput(input) {
    if (input.type === 56 /* InputTypes.Number */)
        return input.asNumber();
    if (input.type === 59 /* InputTypes.Boolean */)
        return input.asBool();
    if (input.type === 58 /* InputTypes.Trigger */)
        return input.asTrigger();
    return input;
}
class RiveSMInput {
    constructor(stateMachine) {
        this.stateMachine = stateMachine;
        this.change = new EventEmitter();
        this.load = new EventEmitter();
    }
    set name(name) {
        if (!name)
            return;
        this._name = name;
        if (this.input)
            return;
        this.init(this.stateMachine.inputs[name]);
    }
    get name() {
        return this.input?.name ?? this._name;
    }
    set value(rawValue) {
        if (typeof rawValue === 'undefined' || rawValue === null)
            return;
        const value = typeof rawValue === 'string'
            ? parseFloat(rawValue)
            : rawValue;
        if (this.input) {
            this.input.value = value;
            this.change.emit(this.input);
        }
        else {
            this._value = value;
        }
    }
    get value() {
        return this.input?.value ?? this._value;
    }
    /** @internal: Used by the RiveStateMachine */
    init(input) {
        if (!input || input.name === this.input?.name)
            return;
        this.input = getInput(input);
        this.load.emit(input);
        if (typeof this._value !== 'undefined') {
            this.input.value = this._value;
            this.change.emit(this.input);
        }
        if (this.shouldFire) {
            this.shouldFire(input);
            delete this.shouldFire;
        }
    }
    fire() {
        const fire = (input) => {
            if (input.type === 58 /* InputTypes.Trigger */) {
                input.fire();
                this.change.emit(input);
            }
        };
        this.input
            ? fire(this.input)
            : this.shouldFire = fire;
    }
}
RiveSMInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveSMInput, deps: [{ token: RiveStateMachine }], target: i0.ɵɵFactoryTarget.Directive });
RiveSMInput.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveSMInput, selector: "riv-input, [rivInput]", inputs: { name: "name", value: "value" }, outputs: { change: "change", load: "load" }, exportAs: ["rivInput"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveSMInput, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-input, [rivInput]',
                    exportAs: 'rivInput'
                }]
        }], ctorParameters: function () { return [{ type: RiveStateMachine }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], change: [{
                type: Output
            }], load: [{
                type: Output
            }] } });
function exist(v) {
    return v !== undefined && v !== null;
}
class RiveStateMachine {
    constructor(zone, canvas, service) {
        this.zone = zone;
        this.canvas = canvas;
        this.service = service;
        this.state = new BehaviorSubject({ speed: 1, playing: false });
        this.inputs = {};
        this.load = new EventEmitter();
        this.stateChange = new EventEmitter();
    }
    set name(name) {
        if (typeof name !== 'string')
            return;
        this.zone.runOutsideAngular(() => {
            this.register(name);
        });
    }
    set index(value) {
        const index = typeof value === 'string' ? parseInt(value) : value;
        if (typeof index !== 'number')
            return;
        this.zone.runOutsideAngular(() => {
            this.register(index);
        });
    }
    set speed(value) {
        const speed = typeof value === 'string' ? parseFloat(value) : value;
        if (typeof speed === 'number')
            this.update({ speed });
    }
    get speed() {
        return this.state.getValue().speed;
    }
    set play(playing) {
        if (playing === true || playing === '') {
            this.update({ playing: true });
        }
        else if (playing === false) {
            this.update({ playing: false });
        }
    }
    get play() {
        return this.state.getValue().playing;
    }
    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.instance?.delete();
    }
    update(state) {
        this.state.next({ ...this.state.getValue(), ...state });
    }
    register(name) {
        // Stop subscribing to previous animation if any
        this.sub?.unsubscribe();
        // Update on frame change if playing
        const onFrameChange = this.state.pipe(switchMap((state) => this.getFrame(state)), filter(exist), map(([state, time]) => this.moveFrame(state, time)));
        // Wait for canvas & animation to be loaded
        this.sub = this.canvas.onReady().pipe(map(() => this.initStateMachine(name)), switchMap(() => onFrameChange)).subscribe((delta) => this.applyChange(delta));
    }
    initStateMachine(name) {
        if (!this.canvas.rive)
            throw new Error('Could not load state machine instance before rive');
        if (!this.canvas.artboard)
            throw new Error('Could not load state machine instance before artboard');
        this.stateMachine = typeof name === 'string'
            ? this.canvas.artboard.stateMachineByName(name)
            : this.canvas.artboard.stateMachineByIndex(name);
        this.instance = new this.canvas.rive.StateMachineInstance(this.stateMachine);
        // Fetch the inputs from the runtime if we don't have them
        for (let i = 0; i < this.instance.inputCount(); i++) {
            this.setInput(this.instance.input(i));
        }
        this.load.emit(this.stateMachine);
    }
    setInput(input) {
        this.inputs[input.name] = input;
        const riveInput = this.riveInputs?.find(item => item.name === input.name);
        if (riveInput) {
            riveInput.init(input);
        }
    }
    getFrame(state) {
        if (state.playing) {
            return this.service.frame.pipe(map((time) => [state, time]));
        }
        else {
            return of(null);
        }
    }
    moveFrame(state, time) {
        return (time / 1000) * state.speed;
    }
    applyChange(delta) {
        if (!this.instance)
            throw new Error('Could not load state machin instance before running it');
        this.canvas.draw(this.instance, delta);
        // Check for any state machines that had a state change
        const changeCount = this.instance.stateChangedCount();
        if (changeCount) {
            const states = [];
            for (let i = 0; i < changeCount; i++) {
                states.push(this.instance.stateChangedNameByIndex(i));
            }
            this.stateChange.emit(states);
        }
    }
}
RiveStateMachine.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveStateMachine, deps: [{ token: i0.NgZone }, { token: RiveCanvasDirective }, { token: RiveService }], target: i0.ɵɵFactoryTarget.Directive });
RiveStateMachine.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.3", type: RiveStateMachine, selector: "riv-state-machine, [rivStateMachine]", inputs: { name: "name", index: "index", speed: "speed", play: "play" }, outputs: { load: "load", stateChange: "stateChange" }, queries: [{ propertyName: "riveInputs", predicate: RiveSMInput }], exportAs: ["rivStateMachine"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveStateMachine, decorators: [{
            type: Directive,
            args: [{
                    selector: 'riv-state-machine, [rivStateMachine]',
                    exportAs: 'rivStateMachine'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: RiveCanvasDirective }, { type: RiveService }]; }, propDecorators: { riveInputs: [{
                type: ContentChildren,
                args: [RiveSMInput]
            }], load: [{
                type: Output
            }], stateChange: [{
                type: Output
            }], name: [{
                type: Input
            }], index: [{
                type: Input
            }], speed: [{
                type: Input
            }], play: [{
                type: Input
            }] } });

class RiveModule {
}
RiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.3", ngImport: i0, type: RiveModule, declarations: [RiveCanvasDirective, RiveAnimationDirective, RivePlayer, RiveNode, RiveBone, RiveRootBone, RiveSMInput, RiveStateMachine], imports: [HttpClientModule], exports: [RiveCanvasDirective, RiveAnimationDirective, RivePlayer, RiveNode, RiveBone, RiveRootBone, RiveSMInput, RiveStateMachine] });
RiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveModule, providers: [RiveService], imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.3", ngImport: i0, type: RiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                    declarations: [RiveCanvasDirective, RiveAnimationDirective, RivePlayer, RiveNode, RiveBone, RiveRootBone, RiveSMInput, RiveStateMachine],
                    exports: [RiveCanvasDirective, RiveAnimationDirective, RivePlayer, RiveNode, RiveBone, RiveRootBone, RiveSMInput, RiveStateMachine],
                    providers: [RiveService]
                }]
        }] });

// interface RiveOptions {
//   locateFile(file: string): string
// }
;
;
;
///////////
// ENUMS //
///////////
var Fit;
(function (Fit) {
    Fit[Fit["fill"] = 0] = "fill";
    Fit[Fit["contain"] = 1] = "contain";
    Fit[Fit["cover"] = 2] = "cover";
    Fit[Fit["fitWidth"] = 3] = "fitWidth";
    Fit[Fit["fitHeight"] = 4] = "fitHeight";
    Fit[Fit["none"] = 5] = "none";
    Fit[Fit["scaleDown"] = 6] = "scaleDown";
})(Fit || (Fit = {}));
var RenderPaintStyle;
(function (RenderPaintStyle) {
    RenderPaintStyle[RenderPaintStyle["fill"] = 0] = "fill";
    RenderPaintStyle[RenderPaintStyle["stroke"] = 1] = "stroke";
})(RenderPaintStyle || (RenderPaintStyle = {}));
var FillRule;
(function (FillRule) {
    FillRule[FillRule["nonZero"] = 0] = "nonZero";
    FillRule[FillRule["evenOdd"] = 1] = "evenOdd";
})(FillRule || (FillRule = {}));
var StrokeCap;
(function (StrokeCap) {
    StrokeCap[StrokeCap["butt"] = 0] = "butt";
    StrokeCap[StrokeCap["round"] = 1] = "round";
    StrokeCap[StrokeCap["square"] = 2] = "square";
})(StrokeCap || (StrokeCap = {}));
var StrokeJoin;
(function (StrokeJoin) {
    StrokeJoin[StrokeJoin["miter"] = 0] = "miter";
    StrokeJoin[StrokeJoin["round"] = 1] = "round";
    StrokeJoin[StrokeJoin["bevel"] = 2] = "bevel";
})(StrokeJoin || (StrokeJoin = {}));
var BlendMode;
(function (BlendMode) {
    BlendMode[BlendMode["srcOver"] = 3] = "srcOver";
    BlendMode[BlendMode["screen"] = 14] = "screen";
    BlendMode[BlendMode["overlay"] = 15] = "overlay";
    BlendMode[BlendMode["darken"] = 16] = "darken";
    BlendMode[BlendMode["lighten"] = 17] = "lighten";
    BlendMode[BlendMode["colorDodge"] = 18] = "colorDodge";
    BlendMode[BlendMode["colorBurn"] = 19] = "colorBurn";
    BlendMode[BlendMode["hardLight"] = 20] = "hardLight";
    BlendMode[BlendMode["softLight"] = 21] = "softLight";
    BlendMode[BlendMode["difference"] = 22] = "difference";
    BlendMode[BlendMode["exclusion"] = 23] = "exclusion";
    BlendMode[BlendMode["multiply"] = 24] = "multiply";
    BlendMode[BlendMode["hue"] = 25] = "hue";
    BlendMode[BlendMode["saturation"] = 26] = "saturation";
    BlendMode[BlendMode["color"] = 27] = "color";
    BlendMode[BlendMode["luminosity"] = 28] = "luminosity";
})(BlendMode || (BlendMode = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { BlendMode, FillRule, Fit, RIVE_FOLDER, RIVE_VERSION, RenderPaintStyle, RiveAnimationDirective, RiveBone, RiveCanvasDirective, RiveModule, RiveNode, RivePlayer, RiveRootBone, RiveSMInput, RiveService, RiveStateMachine, RiveTransformComponent, StrokeCap, StrokeJoin, enterZone, frameToSec, round };
